import type { HowItWorksStep, Benefit, FaqItem } from './types';

// ─── Target-size templates ──────────────────────────────────────────────────

interface TargetSizeContent {
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  subtitle: string;
  introParagraph: string;
  seoIntro: string;
  howItWorksSteps: HowItWorksStep[];
  benefits: Benefit[];
  faqItems: FaqItem[];
}

export function targetSizeTemplate(size: string, keyword: 'imagen' | 'foto'): TargetSizeContent {
  const kw = keyword === 'foto' ? 'foto' : 'imagen';
  const kwPlural = keyword === 'foto' ? 'fotos' : 'imágenes';
  const kwUpper = keyword === 'foto' ? 'Foto' : 'Imagen';

  // Determine context based on size for unique content
  const sizeNum = parseFloat(size);
  const unit = size.toLowerCase().includes('mb') ? 'MB' : 'KB';
  const isSmall = unit === 'KB' && sizeNum <= 75;
  const isMedium = unit === 'KB' && sizeNum > 75 && sizeNum <= 500;
  const isLarge = unit === 'MB';

  const contextPhrase = isSmall
    ? 'firmas digitales, avatares y formularios con requisitos estrictos'
    : isMedium
    ? 'formularios online, trámites y plataformas web'
    : 'plataformas de email, portfolios y CMS';

  const qualityNote = isSmall
    ? 'Para mejores resultados con tamaños pequeños, recomendamos reducir también las dimensiones de la imagen.'
    : isMedium
    ? `A ${size}, la calidad visual se mantiene excelente para la mayoría de usos digitales.`
    : `${size} permite mantener una calidad fotográfica muy alta, ideal para uso profesional.`;

  return {
    title: `Reducir ${kw} a ${size}`,
    h1: `Reducir ${kw} a ${size} online gratis`,
    metaTitle: `Reducir ${kw} a ${size} Online Gratis — Comprime al tamaño exacto | CompresorJPEG`,
    metaDescription: `Reduce el tamaño de tus ${kwPlural} a ${size} o menos online gratis. Perfecto para ${contextPhrase}. Sin registro.`,
    subtitle: `Comprime tus ${kwPlural} para que pesen ${size} o menos.`,
    introParagraph: `¿Necesitas que tu ${kw} pese menos de ${size}? Es un requisito habitual en ${contextPhrase}. Con nuestra herramienta puedes reducir cualquier ${kw} al tamaño requerido de forma rápida y gratuita, directamente desde tu navegador.`,
    seoIntro: `Reducir una ${kw} a ${size} es una necesidad frecuente para ${contextPhrase}. ${qualityNote} Nuestra herramienta de compresión te permite ajustar la calidad hasta alcanzar exactamente ${size} o menos, compatible con archivos JPG, PNG y WebP.`,
    howItWorksSteps: [
      { number: '1', title: `Sube tu ${kw}`, description: `Arrastra y suelta la ${kw} que necesitas reducir a ${size}.` },
      { number: '2', title: 'Ajusta la compresión', description: `Mueve el control de calidad hasta que el tamaño sea menor a ${size}.` },
      { number: '3', title: 'Descarga el resultado', description: `Descarga tu ${kw} reducida lista para usar donde la necesites.` },
    ],
    benefits: selectBenefits(size, kw, kwPlural, contextPhrase, isSmall, isMedium),
    faqItems: selectTargetSizeFaqs(size, kw, kwPlural, isSmall, isMedium, isLarge),
  };
}

function selectBenefits(size: string, kw: string, kwPlural: string, context: string, isSmall: boolean, isMedium: boolean): Benefit[] {
  const pool: Benefit[] = [
    { title: 'Tamaño exacto', description: `Controla la compresión para alcanzar exactamente el límite de ${size}.` },
    { title: `Ideal para ${isSmall ? 'firmas e iconos' : isMedium ? 'formularios' : 'email y web'}`, description: `Perfecto para cumplir los requisitos de ${context}.` },
    { title: 'Múltiples formatos', description: `Funciona con JPG, PNG y WebP para adaptarse a cualquier requisito de ${kw}.` },
    { title: 'Vista previa en tiempo real', description: `Verifica cómo queda tu ${kw} antes de descargarla.` },
    { title: 'Procesamiento local', description: `Tus ${kwPlural} se procesan en tu navegador sin subirse a ningún servidor.` },
    { title: 'Sin registro necesario', description: `Comprime tus ${kwPlural} de forma gratuita sin crear una cuenta.` },
  ];
  // Return first 4
  return pool.slice(0, 4);
}

function selectTargetSizeFaqs(size: string, kw: string, kwPlural: string, isSmall: boolean, isMedium: boolean, isLarge: boolean): FaqItem[] {
  const faqs: FaqItem[] = [
    {
      question: `¿Cómo reduzco una ${kw} a ${size}?`,
      answer: `Sube tu ${kw} y ajusta el control de calidad. El tamaño resultante se muestra en tiempo real para que puedas alcanzar exactamente ${size} o menos.`,
    },
    {
      question: `¿Se pierde calidad al reducir a ${size}?`,
      answer: isSmall
        ? `Depende de las dimensiones originales. Para ${kwPlural} pequeñas la calidad se mantiene aceptable. Para mejores resultados, reduce también las dimensiones.`
        : isMedium
        ? `La pérdida es mínima. A ${size}, la calidad visual es más que suficiente para documentos, formularios y uso web.`
        : `Prácticamente no. ${size} es un tamaño generoso que permite mantener una calidad excelente para la mayoría de usos.`,
    },
    {
      question: `¿Qué formatos puedo reducir a ${size}?`,
      answer: `Puedes reducir ${kwPlural} en formato JPG, PNG y WebP. El formato JPG suele ofrecer los mejores resultados para tamaños pequeños.`,
    },
    {
      question: `¿Es seguro comprimir mis ${kwPlural} aquí?`,
      answer: `Sí, completamente. Todas las ${kwPlural} se procesan localmente en tu navegador y nunca se suben a nuestros servidores. Tu privacidad está garantizada.`,
    },
  ];
  return faqs;
}

// ─── Compression synonym templates ──────────────────────────────────────────

interface CompressionContent {
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  subtitle: string;
  introParagraph: string;
  seoIntro: string;
  howItWorksSteps: HowItWorksStep[];
  benefits: Benefit[];
  faqItems: FaqItem[];
}

export function compressionSynonymTemplate(keyword: string, displayKeyword: string): CompressionContent {
  return {
    title: displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1),
    h1: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} — herramienta online gratuita`,
    metaTitle: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} Online Gratis — Sin perder calidad | CompresorJPEG`,
    metaDescription: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} online gratis. Reduce el peso de JPG, PNG y WebP sin perder calidad. Procesamiento local, rápido y sin registro.`,
    subtitle: `Reduce el peso de tus imágenes de forma rápida, segura y gratuita.`,
    introParagraph: `Nuestra herramienta te permite ${displayKeyword} de forma instantánea. Funciona con archivos JPG, PNG y WebP directamente en tu navegador, sin necesidad de instalar software ni crear una cuenta. El procesamiento es completamente local: tus archivos nunca salen de tu dispositivo.`,
    seoIntro: `Si buscas ${displayKeyword}, has llegado al lugar indicado. CompresorJPEG es una herramienta 100% online y gratuita que analiza cada imagen para encontrar el nivel de compresión óptimo. Reduce el peso de tus archivos hasta un 90% sin pérdida visual perceptible, ideal para optimizar tu web, enviar archivos por email o ahorrar espacio de almacenamiento.`,
    howItWorksSteps: [
      { number: '1', title: 'Sube tus imágenes', description: 'Arrastra y suelta tus archivos en cualquier formato: JPG, PNG o WebP.' },
      { number: '2', title: 'Compresión automática', description: 'Nuestro algoritmo analiza cada imagen y aplica la compresión ideal.' },
      { number: '3', title: 'Descarga los resultados', description: 'Descarga tus imágenes comprimidas individualmente o todas en un ZIP.' },
    ],
    benefits: [
      { title: 'Sin pérdida visible', description: 'Algoritmos inteligentes que reducen el peso sin afectar la calidad visual perceptible.' },
      { title: '100% en el navegador', description: 'Todo el procesamiento ocurre en tu dispositivo. Tus imágenes nunca se suben a ningún servidor.' },
      { title: 'Sin registro ni límites', description: 'Comprime todas las imágenes que necesites sin crear cuenta ni limitaciones.' },
      { title: 'Múltiples archivos a la vez', description: 'Sube y comprime varias imágenes simultáneamente para ahorrar tiempo.' },
    ],
    faqItems: [
      {
        question: `¿Es gratis ${displayKeyword}?`,
        answer: 'Sí, nuestra herramienta es completamente gratuita. No hay costos ocultos, marcas de agua ni limitaciones en la cantidad de imágenes.',
      },
      {
        question: '¿Se pierde calidad al comprimir?',
        answer: 'La pérdida de calidad es mínima e imperceptible en la mayoría de los casos. Puedes ajustar el nivel de compresión según tus necesidades de calidad.',
      },
      {
        question: '¿Qué formatos de imagen son compatibles?',
        answer: 'Nuestra herramienta es compatible con JPG, JPEG, PNG y WebP, los formatos más utilizados en fotografía digital y diseño web.',
      },
      {
        question: '¿Necesito instalar algún programa?',
        answer: 'No, la herramienta funciona completamente online en tu navegador web. No necesitas descargar ni instalar nada.',
      },
    ],
  };
}

// ─── Use-case templates ─────────────────────────────────────────────────────

interface UseCaseContent {
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  subtitle: string;
  introParagraph: string;
  seoIntro: string;
  howItWorksSteps: HowItWorksStep[];
  benefits: Benefit[];
  faqItems: FaqItem[];
}

export function useCaseTemplate(useCase: string, contextLabel: string, detailText: string): UseCaseContent {
  const isOptimization = useCase.startsWith('optimizar');

  const verb = isOptimization ? 'Optimizar' : 'Comprimir';
  const verbLower = isOptimization ? 'optimizar' : 'comprimir';
  const noun = isOptimization ? 'optimización' : 'compresión';

  return {
    title: `${verb} imagen para ${contextLabel}`,
    h1: `${verb} imágenes para ${contextLabel} online gratis`,
    metaTitle: `${verb} imagen para ${contextLabel} Online Gratis | CompresorJPEG`,
    metaDescription: `${verb} tus imágenes para ${contextLabel}. ${detailText} Herramienta gratuita online sin registro.`,
    subtitle: `${isOptimization ? 'Optimiza' : 'Reduce el peso de'} tus imágenes para ${contextLabel.toLowerCase()}.`,
    introParagraph: `${detailText} Con nuestra herramienta puedes ${verbLower} tus imágenes de forma rápida y precisa, directamente desde tu navegador y sin perder la calidad necesaria.`,
    seoIntro: `La ${noun} de imágenes para ${contextLabel.toLowerCase()} es esencial para obtener los mejores resultados. Nuestra herramienta analiza cada imagen y aplica el nivel de ${noun} óptimo, asegurando que tus archivos cumplan con los requisitos de tamaño y calidad de la plataforma de destino.`,
    howItWorksSteps: [
      { number: '1', title: 'Sube tus imágenes', description: `Arrastra y suelta las imágenes que necesitas ${verbLower} para ${contextLabel.toLowerCase()}.` },
      { number: '2', title: `${isOptimization ? 'Optimización' : 'Compresión'} inteligente`, description: `Nuestro algoritmo aplica la ${noun} ideal para ${contextLabel.toLowerCase()}.` },
      { number: '3', title: 'Descarga y usa', description: `Descarga tus imágenes ${isOptimization ? 'optimizadas' : 'comprimidas'} listas para usar.` },
    ],
    benefits: [
      { title: `Ideal para ${contextLabel}`, description: `${isOptimization ? 'Optimización' : 'Compresión'} ajustada a los requisitos de ${contextLabel.toLowerCase()}.` },
      { title: 'Calidad preservada', description: `Mantiene la calidad visual necesaria para que tus imágenes se vean profesionales.` },
      { title: 'Procesamiento local', description: 'Tus imágenes se procesan en tu navegador sin subirse a ningún servidor externo.' },
      { title: 'Sin registro ni instalación', description: 'Funciona directamente en el navegador, gratis y sin crear cuenta.' },
    ],
    faqItems: [
      {
        question: `¿Qué tamaño deben tener las imágenes para ${contextLabel.toLowerCase()}?`,
        answer: `Depende de la plataforma o uso específico. Como regla general, imágenes entre 100KB y 500KB ofrecen un buen equilibrio entre calidad y peso para la mayoría de usos.`,
      },
      {
        question: `¿Se pierde calidad al ${verbLower} para ${contextLabel.toLowerCase()}?`,
        answer: `La pérdida es mínima e imperceptible. Nuestra herramienta busca el mejor equilibrio entre tamaño y calidad para cada imagen.`,
      },
      {
        question: `¿Puedo ${verbLower} varias imágenes a la vez?`,
        answer: `Sí, puedes subir y ${verbLower} múltiples imágenes simultáneamente y descargarlas todas en un ZIP.`,
      },
      {
        question: '¿Es seguro usar esta herramienta?',
        answer: 'Sí, completamente. Todo el procesamiento ocurre en tu navegador. Tus imágenes nunca se envían a ningún servidor.',
      },
    ],
  };
}
